import { useState, useEffect, useCallback } from 'react'
import { apiService } from '../../../services/api'
import DashboardCard from '../shared/DashboardCard'
import StatCard from '../shared/StatCard'
import {
  UserIcon,
  ChartBarIcon,
  TrophyIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  ArrowPathIcon,
  LinkIcon,
} from '@heroicons/react/24/outline'

/*
 * Wired to what the API actually returns (#160). This dashboard used to be
 * built around its demo fixture: it read `child.name`/`grade`/`gpa` from a
 * `/consent/my-children` row that has none of them, used the *consent* id as
 * the athlete id when fetching performance, and read `stats` and
 * `recentResults` from a performance response that returns `performance` and
 * `summary`. On the real path every tile was 0 or "--" and every panel empty,
 * forever. Academic progress, upcoming events and college interest had no
 * source anywhere in the API and are gone rather than invented.
 */

/** One row of `GET /api/consent/my-children`. */
interface ChildLink {
  id: string // the consent's id, not the athlete's
  consentType: string
  athlete: {
    id: string
    firstName: string
    lastName: string
    school: string | null
    positions: Array<{ sport: string; position: string; isPrimary: boolean }>
  }
}

/** `GET /api/athletes/:id/performance`. */
interface PerformanceResponse {
  athleteId: string
  performance: Array<{
    id: string
    drill: string
    value: number
    unit: string
    percentile: number | null
    event: string
    date: string
    verified: boolean
  }>
  summary: {
    totalResults: number
    averagePercentile: number | null
    recentTrend: 'improving' | 'declining' | 'stable' | 'insufficient_data'
  }
}

const TREND_LABEL: Record<PerformanceResponse['summary']['recentTrend'], string> = {
  improving: 'Improving',
  declining: 'Declining',
  stable: 'Stable',
  insufficient_data: 'Not enough data',
}

const childName = (c: ChildLink) => `${c.athlete.firstName} ${c.athlete.lastName}`.trim()
const primaryPosition = (c: ChildLink) =>
  (c.athlete.positions.find(p => p.isPrimary) ?? c.athlete.positions[0])?.position

/** Percentiles are ranked against athletes at the same position. */
function percentileCaption(percentile: number, position?: string): string {
  const cohort = position ? `${position}s` : 'athletes at their position'
  if (percentile >= 90) {
    return `Top 10% of ${cohort}`
  }
  if (percentile >= 75) {
    return `Top 25% of ${cohort}`
  }
  if (percentile > 50) {
    return `Above the median for ${cohort}`
  }
  if (percentile === 50) {
    return `At the median for ${cohort}`
  }
  return `Below the median for ${cohort}`
}

function percentileColor(percentile: number) {
  if (percentile >= 90) {
    return 'text-green-600 bg-green-50'
  }
  if (percentile >= 75) {
    return 'text-blue-600 bg-blue-50'
  }
  if (percentile >= 50) {
    return 'text-yellow-600 bg-yellow-50'
  }
  return 'text-gray-600 bg-gray-50'
}

export default function ParentDashboard() {
  const [children, setChildren] = useState<ChildLink[]>([])
  const [selectedChild, setSelectedChild] = useState<ChildLink | null>(null)
  const [performance, setPerformance] = useState<PerformanceResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [perfLoading, setPerfLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [perfError, setPerfError] = useState<string | null>(null)

  const fetchChildren = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiService.getMyChildren()
      if (response.error) {
        setError(response.message || 'Failed to load linked athletes')
        setChildren([])
        return
      }
      const list: ChildLink[] = Array.isArray(response.data) ? response.data : []
      setChildren(list)
      setSelectedChild(list[0] ?? null)
    } catch {
      setError('Failed to load linked athletes')
      setChildren([])
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchPerformance = useCallback(async (athleteId: string) => {
    try {
      setPerfLoading(true)
      setPerfError(null)
      const response = await apiService.getAthletePerformance(athleteId)
      if (response.error || !response.data) {
        setPerformance(null)
        setPerfError(response.message || 'Failed to load results')
      } else {
        setPerformance(response.data as PerformanceResponse)
      }
    } catch {
      setPerformance(null)
      setPerfError('Failed to load results')
    } finally {
      setPerfLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchChildren()
  }, [fetchChildren])

  useEffect(() => {
    if (selectedChild) {
      // The athlete's id — not the consent row's.
      fetchPerformance(selectedChild.athlete.id)
    }
  }, [selectedChild, fetchPerformance])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <p className="text-sm text-red-700">{error}</p>
          <button
            onClick={fetchChildren}
            className="flex items-center space-x-1 text-sm text-red-700 hover:text-red-900 font-medium"
          >
            <ArrowPathIcon className="h-4 w-4" />
            <span>Retry</span>
          </button>
        </div>
      </div>
    )
  }

  if (children.length === 0) {
    return (
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Parent Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">Follow your athlete's combine results</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
          <LinkIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No athlete linked yet</h3>
          {/* This used to direct parents to a "Settings → Link Athlete" screen
              and a "linking code", neither of which exists. */}
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            Your account is not linked to an athlete. Ask your athlete's coach or event organizer to
            link your accounts; their results will appear here once they are.
          </p>
        </div>
      </div>
    )
  }

  const results = performance?.performance ?? []
  const summary = performance?.summary
  const eventsAttended = new Set(results.map(r => r.event)).size
  const position = selectedChild ? primaryPosition(selectedChild) : undefined

  const childStats = [
    {
      name: 'Results Recorded',
      value: summary ? summary.totalResults : '--',
      icon: ChartBarIcon,
    },
    {
      name: 'Average Percentile',
      // Unranked results are not counted as zero; with none ranked it is "--".
      value:
        summary?.averagePercentile !== null && summary?.averagePercentile !== undefined
          ? `${Math.round(summary.averagePercentile)}th`
          : '--',
      icon: TrophyIcon,
    },
    {
      name: 'Events Attended',
      value: performance ? eventsAttended : '--',
      icon: CalendarIcon,
    },
    {
      name: 'Recent Trend',
      value: summary ? TREND_LABEL[summary.recentTrend] : '--',
      icon: ArrowTrendingUpIcon,
    },
  ]

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Parent Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">
          Follow {selectedChild ? childName(selectedChild) : 'your athlete'}'s combine results
        </p>
      </div>

      {children.length > 1 && (
        <div className="flex space-x-2">
          {children.map(child => (
            <button
              key={child.id}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                selectedChild?.id === child.id
                  ? 'bg-primary-100 text-primary-800 border border-primary-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
              }`}
              onClick={() => setSelectedChild(child)}
            >
              {childName(child)}
            </button>
          ))}
        </div>
      )}

      {selectedChild && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-blue-200 rounded-full flex items-center justify-center">
                <UserIcon className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900">{childName(selectedChild)}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-2 text-sm">
                <div>
                  <span className="text-gray-500">Position:</span>
                  <p className="font-medium">{position || '--'}</p>
                </div>
                <div>
                  <span className="text-gray-500">School:</span>
                  <p className="font-medium">{selectedChild.athlete.school || '--'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Your access:</span>
                  <p className="font-medium">
                    {selectedChild.consentType === 'FULL_CONTROL' ? 'Full control' : 'View only'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {childStats.map(stat => (
          <StatCard
            key={stat.name}
            name={stat.name}
            value={stat.value}
            icon={stat.icon}
            loading={perfLoading}
          />
        ))}
      </div>

      <DashboardCard title="Recent Results" loading={perfLoading} error={perfError ?? undefined}>
        {results.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-gray-500 text-sm">No results recorded yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {results.slice(0, 10).map(result => (
              <div
                key={result.id}
                className="flex items-center justify-between border border-gray-200 rounded-lg p-3"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {result.drill}: {result.value} {result.unit}
                  </p>
                  <p className="text-xs text-gray-500">
                    {result.event} · {new Date(result.date).toLocaleDateString()}
                    {!result.verified && ' · not verified'}
                  </p>
                  {result.percentile !== null && (
                    <p className="text-xs text-gray-500">
                      {percentileCaption(result.percentile, position)}
                    </p>
                  )}
                </div>
                {result.percentile !== null ? (
                  <span
                    className={`px-2 py-1 rounded text-sm font-semibold ${percentileColor(result.percentile)}`}
                  >
                    {Math.round(result.percentile)}th
                  </span>
                ) : (
                  <span className="px-2 py-1 rounded text-xs text-gray-500 bg-gray-50">
                    Unranked
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </DashboardCard>
    </div>
  )
}
